import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { dashboard, suppliers as suppliersApi } from "@/api";
import TierBadge from "@/components/TierBadge";
import { GemLegend } from "@/components/GemLegend";
import { TIER_META, PILLARS } from "@/constants";
import { useCategories } from "@/hooks/useCategories";
import { Award } from "lucide-react";
import { useAuth } from "@/auth";
import CompletenessHeatmap from "@/components/CompletenessHeatmap";
import CaptureProgressOverview from "@/components/CaptureProgressOverview";
import DevelopmentWarnings from "@/components/DevelopmentWarnings";
import WeeklyDigest from "@/components/WeeklyDigest";
import WelcomePanel from "@/components/WelcomePanel";
import TrendsPanel from "@/components/TrendsPanel";

function StatCard({ label, value, sub, accent = "var(--lala-blue)" }) {
  return (
    <div className="sketch-border bg-[var(--cream-soft)] p-5">
      <div className="text-xs uppercase tracking-wider text-[var(--ink-soft)] font-semibold">{label}</div>
      <div className="font-display text-4xl mt-1" style={{ color: accent }}>{value}</div>
      {sub && <div className="text-xs text-[var(--ink-soft)] mt-1">{sub}</div>}
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const CATEGORIES = useCategories();
  const [stats, setStats] = useState(null);
  const [supps, setSupps] = useState([]);
  const [category, setCategory] = useState("");
  const [tier, setTier] = useState("");
  const [q, setQ] = useState("");
  const [progressView, setProgressView] = useState("new");

  const load = async (retry = 0) => {
    try {
      const [s, sp] = await Promise.all([
        dashboard.stats(category ? { category } : {}),
        suppliersApi.list({ ...(category && { category }), ...(tier && { tier }), ...(q && { q }) }),
      ]);
      setStats(s);
      setSupps(sp);
    } catch (e) {
      if (retry < 2) setTimeout(() => load(retry + 1), 1200);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [category, tier, q]);

  return (
    <div className="max-w-7xl mx-auto px-6 py-10">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
        <div>
          <div className="font-hand text-xl text-[var(--lala-blue)]">cuadro de mando</div>
          <h1 className="font-display text-4xl md:text-5xl" data-testid="dashboard-title">Vista general</h1>
          <p className="text-[var(--ink-soft)] mt-1">Visibilidad por pilar, categoría y proveedor.</p>
        </div>
      </div>

      <WelcomePanel />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8" data-testid="stat-cards">
        <StatCard label="Proveedores totales" value={stats?.total_suppliers ?? "—"} sub={stats ? `${stats.evaluated_suppliers ?? 0} evaluados` : "cargando…"} />
        <StatCard label="Promedio global" value={stats?.average_score?.toFixed?.(2) ?? "—"} sub="escala 0–10" accent="#11142B" />
        <StatCard label="Nivel Partner" value={stats ? (stats.tier_counts?.partner ?? 0) : "—"} sub="proveedores" accent={TIER_META.partner.color} />
        <StatCard label="Nivel Key" value={stats ? (stats.tier_counts?.key ?? 0) : "—"} sub="proveedores" accent={TIER_META.key.color} />
      </div>

      <GemLegend />

      {user?.role === "admin" && (
        <div className="flex gap-2 mb-3" data-testid="progress-view-toggle">
          {[["new", "Vista nueva"], ["old", "Vista anterior"]].map(([k, l]) => (
            <button key={k} type="button" onClick={() => setProgressView(k)}
              className={`text-xs font-bold px-3 py-1.5 rounded-full border ${progressView === k ? "bg-[var(--lala-blue)] text-white border-[var(--lala-blue)]" : "bg-white border-[var(--line)] text-[var(--ink-soft)]"}`}>
              {l}
            </button>
          ))}
        </div>
      )}
      {progressView === "new" ? <CaptureProgressOverview /> : <CompletenessHeatmap />}

      <DevelopmentWarnings />

      {(user?.role === "admin" || user?.role === "owner") && <WeeklyDigest />}

      <TrendsPanel />

      <div className="grid md:grid-cols-2 gap-6 mb-10">
        <div className="sketch-border bg-[var(--cream-soft)] p-6">
          <div className="font-hand text-xl text-[var(--lala-blue)]">promedios por pilar</div>
          <div className="space-y-3 mt-3">
            {PILLARS.map((p) => {
              const v = stats?.pillar_averages?.[p.key] ?? 0;
              return (
                <div key={p.key}>
                  <div className="flex justify-between items-center text-sm font-semibold">
                    <span className="flex items-center gap-2">
                      <img
                        src={`/brand/icon_pilar_${p.key === "relacion" ? "servicio" : p.key}.png`}
                        alt=""
                        className="w-6 h-6 object-contain"
                        data-testid={`pillar-bar-icon-${p.key}`}
                        draggable={false}
                      />
                      <Link
                        to={`/dashboard/pilar/${p.key}`}
                        className="hover:underline text-[var(--lala-blue)]"
                        data-testid={`pillar-link-${p.key}`}
                        title={`Ver ranking por ${p.label}`}
                      >
                        {p.label} →
                      </Link>
                    </span>
                    <span>{v.toFixed(2)}</span>
                  </div>
                  <div className="h-2 mt-1 rounded-full bg-[var(--cream-deep)]">
                    <div className="h-2 rounded-full" style={{ width: `${(v / 10) * 100}%`, background: "var(--lala-blue)" }} />
                  </div>
                </div>
              );
            })}
          </div>
          <p className="text-xs text-[var(--ink-soft)] mt-4">
            <span className="font-hand text-base text-[var(--lala-blue)]">tip · </span>
            Haz clic en cualquier pilar para abrir la <strong>vista filtrada</strong> con el ranking de proveedores en ese pilar.
          </p>
        </div>
        <div className="sketch-border bg-[var(--cream-soft)] p-6">
          <div className="font-hand text-xl text-[var(--lala-blue)]">distribución por nivel</div>
          <div className="space-y-3 mt-3">
            {["recuperacion", "commercial", "challenge", "key", "partner"].map((t) => {
              const meta = TIER_META[t];
              const c = stats?.tier_counts?.[t] ?? 0;
              const total = stats?.total_suppliers || 1;
              return (
                <div key={t}>
                  <div className="flex justify-between items-center text-sm font-semibold">
                    <span className="flex items-center gap-2" style={{ color: meta.color }}>
                      {t !== "recuperacion" && (
                        <img
                          src={`/brand/icon_tier_${t}_metal.png`}
                          alt=""
                          className="w-6 h-6 object-contain"
                          data-testid={`tier-bar-icon-${t}`}
                          draggable={false}
                        />
                      )}
                      {t === "recuperacion" && <span className="w-6 text-center">⚠</span>}
                      {meta.label}
                    </span>
                    <span>{c} · {Math.round((c / total) * 100)}%</span>
                  </div>
                  <div className="h-2 mt-1 rounded-full bg-[var(--cream-deep)]">
                    <div className="h-2 rounded-full" style={{ width: `${(c / total) * 100}%`, background: meta.color }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {stats?.category_leaders?.length > 0 && (
        <div className="sketch-border bg-[var(--cream-soft)] p-6 mb-10" data-testid="category-leaders">
          <div className="font-hand text-xl text-[var(--lala-blue)]">distintivo por score total</div>
          <h3 className="font-display text-2xl mb-3">Líderes por categoría</h3>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {stats.category_leaders.map((l) => (
              <Link
                key={l.category}
                to={`/proveedores/${l.supplier_id}`}
                className="bg-white rounded-xl p-4 border border-[var(--line)] hover:shadow-sm transition-shadow"
                data-testid={`category-leader-${l.category}`}
              >
                <div className="text-[10px] uppercase tracking-wider text-[var(--ink-soft)] font-bold">{l.category}</div>
                <div className="font-semibold text-[var(--ink)] mt-0.5">{l.name}</div>
                <div className="font-display text-2xl text-[var(--lala-blue)] mt-1">
                  {l.score_100}<span className="text-sm text-[var(--ink-soft)]"> /100</span>
                  <span className="text-xs text-[var(--ink-soft)] font-normal ml-2">{l.score} /10</span>
                </div>
                {l.earns_badge && (
                  <span className="inline-flex items-center gap-1 mt-2 px-2.5 py-1 rounded-full text-[10px] font-bold text-white uppercase tracking-wide" style={{ background: "#D4A437" }} data-testid={`category-leader-badge-${l.category}`}>
                    <Award size={11} /> {stats.score_badge_label || "Mejor de su Categoría"}
                  </span>
                )}
              </Link>
            ))}
          </div>
          <p className="text-xs text-[var(--ink-soft)] mt-3">
            El distintivo se otorga al N° 1 de cada categoría que supere el score mínimo configurado en
            {" "}<Link to="/admin/programa" className="text-[var(--lala-blue)] hover:underline">Ajustes del programa</Link>.
          </p>
        </div>
      )}

      <div className="flex flex-wrap gap-3 mb-4" data-testid="filters">
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar proveedor…" className="input-field max-w-xs" data-testid="filter-search" />
        <select value={category} onChange={(e) => setCategory(e.target.value)} className="input-field max-w-[200px]" data-testid="filter-category">
          <option value="">Todas las categorías</option>
          {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <select value={tier} onChange={(e) => setTier(e.target.value)} className="input-field max-w-[200px]" data-testid="filter-tier">
          <option value="">Todos los niveles</option>
          {Object.entries(TIER_META).map(([k, m]) => <option key={k} value={k}>{m.label}</option>)}
        </select>
      </div>

      <div className="sketch-border bg-[var(--cream-soft)] overflow-hidden" data-testid="suppliers-table">
        <table className="w-full text-sm">
          <thead className="bg-[var(--cream-deep)] text-[var(--ink)]">
            <tr className="text-left">
              <th className="px-4 py-3 font-semibold">Proveedor</th>
              <th className="px-4 py-3 font-semibold">Categoría</th>
              <th className="px-4 py-3 font-semibold">Score</th>
              <th className="px-4 py-3 font-semibold">Nivel</th>
              <th className="px-4 py-3 font-semibold">Último período</th>
              <th className="px-4 py-3 font-semibold"></th>
            </tr>
          </thead>
          <tbody>
            {supps.map((s) => (
              <tr key={s.id} className="border-t border-[var(--line)] hover:bg-[var(--cream)]" data-testid={`supplier-row-${s.id}`}>
                <td className="px-4 py-3 font-semibold">{s.name}</td>
                <td className="px-4 py-3 text-[var(--ink-soft)]">{s.category}</td>
                <td className="px-4 py-3 font-display text-xl">{s.current_score?.toFixed?.(2) ?? "—"}</td>
                <td className="px-4 py-3"><TierBadge tier={s.current_tier} /></td>
                <td className="px-4 py-3 text-[var(--ink-soft)]">{s.last_period || "—"}</td>
                <td className="px-4 py-3 text-right">
                  <Link to={`/proveedores/${s.id}`} className="text-[var(--lala-blue)] font-semibold hover:underline" data-testid={`view-supplier-${s.id}`}>
                    Ver detalle →
                  </Link>
                </td>
              </tr>
            ))}
            {supps.length === 0 && (
              <tr><td colSpan="6" className="px-4 py-10 text-center text-[var(--ink-soft)]">Sin proveedores con esos filtros.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
